import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useId, useState, type ReactElement } from 'react';
import { DAdminShell } from '@/components/organisms/admin-shell';
import { FormInput } from '@/components/molecules/form-input';
import { Micro } from '@/components/atoms/micro';
import { Icon } from '@/components/atoms/icon';
import { useModerationCandidate, type ModerationCandidatePiece } from '@/lib/queries/moderation';
import { useUpdatePiece } from '@/lib/queries/pieces';
import { PIECE_REGIONS, type PieceRegion } from '@/lib/types';
import { emitToast } from '@/lib/toast-bus';

const TITLE_REQUIRED_ERROR = 'Title is required';
const CANONICAL_REQUIRED_ERROR = 'Canonical name is required';
const SAVE_SUCCESS_MESSAGE = 'Piece updated';
const SAVE_ERROR_MESSAGE = 'Could not save. Try again.';
const BACK_LINK_LABEL = 'Back to submission';
const NO_REGION_VALUE = '';
const SUMMARY_MAX_LENGTH = 240;

type PieceEditSearch = {
  fromCandidate?: string;
  token?: string;
};

const REGION_LABELS: Record<PieceRegion, string> = {
  IRELAND: 'Ireland',
  SCOTLAND: 'Scotland',
  CAPE_BRETON: 'Cape Breton',
  BRITTANY: 'Brittany',
  ENGLAND: 'England',
  GALICIA: 'Galicia',
  ASTURIAS: 'Asturias',
  OTHER: 'Other',
};

function backLink(fromCandidate: string | undefined): ReactElement {
  return fromCandidate ? (
    <Link
      to="/admin/queue/$pieceId"
      params={{ pieceId: fromCandidate }}
      className="text-[13px] text-[var(--accent)] hover:underline"
    >
      ← {BACK_LINK_LABEL}
    </Link>
  ) : (
    <Link to="/admin/queue" className="text-[13px] text-[var(--accent)] hover:underline">
      ← {BACK_LINK_LABEL}
    </Link>
  );
}

type StringListEditorProps = {
  legend: string;
  addLabel: string;
  values: string[];
  onChange: (next: string[]) => void;
};

function StringListEditor({ legend, addLabel, values, onChange }: StringListEditorProps): ReactElement {
  const groupId = useId();
  const setAt = (index: number, value: string): void => {
    onChange(values.map((current, i) => (i === index ? value : current)));
  };
  const removeAt = (index: number): void => {
    onChange(values.filter((_, i) => i !== index));
  };
  return (
    <fieldset className="flex flex-col gap-1.5" aria-labelledby={groupId}>
      <Micro as="span" id={groupId}>
        {legend}
      </Micro>
      <div className="flex flex-col gap-2">
        {values.map((value, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              aria-label={`${legend} ${index + 1}`}
              value={value}
              onChange={(event) => setAt(index, event.target.value)}
              className="flex-1 h-11 px-3 bg-[var(--bg-surface)] border border-[var(--rule)] rounded-[var(--r-sm)] text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)]"
            />
            <button
              type="button"
              aria-label={`Remove ${legend} ${index + 1}`}
              onClick={() => removeAt(index)}
              className="inline-flex items-center justify-center h-9 w-9 rounded-[var(--r-sm)] text-[var(--ink-3)] hover:text-[var(--err)]"
            >
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...values, ''])}
          className="self-start text-[13px] text-[var(--accent)] hover:underline"
        >
          + {addLabel}
        </button>
      </div>
    </fieldset>
  );
}

type PieceEditFormProps = {
  piece: ModerationCandidatePiece;
  fromCandidate: string | undefined;
};

function PieceEditForm({ piece, fromCandidate }: PieceEditFormProps): ReactElement {
  const navigate = useNavigate();
  const updatePiece = useUpdatePiece();
  const regionSelectId = useId();

  const [prettyName, setPrettyName] = useState(piece.prettyName);
  const [canonicalName, setCanonicalName] = useState(piece.canonicalName);
  const [summary, setSummary] = useState(piece.summary ?? '');
  const [region, setRegion] = useState<PieceRegion | ''>(piece.region ?? NO_REGION_VALUE);
  const [forms, setForms] = useState<string[]>(piece.forms ?? []);
  const [aka, setAka] = useState<string[]>(piece.aka ?? []);
  const [prettyNameError, setPrettyNameError] = useState<string | null>(null);
  const [canonicalNameError, setCanonicalNameError] = useState<string | null>(null);

  const cleanedList = (items: string[]): string[] =>
    items.map((item) => item.trim()).filter((item) => item.length > 0);

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    const trimmedPretty = prettyName.trim();
    const trimmedCanonical = canonicalName.trim();
    setPrettyNameError(trimmedPretty ? null : TITLE_REQUIRED_ERROR);
    setCanonicalNameError(trimmedCanonical ? null : CANONICAL_REQUIRED_ERROR);
    if (!trimmedPretty || !trimmedCanonical) return;

    updatePiece.mutate(
      {
        pieceId: piece.pieceId,
        prettyName: trimmedPretty,
        canonicalName: trimmedCanonical,
        summary: summary.trim() === '' ? null : summary.trim(),
        region: region === NO_REGION_VALUE ? null : region,
        forms: cleanedList(forms),
        aka: cleanedList(aka),
      },
      {
        onSuccess: () => {
          emitToast({ message: SAVE_SUCCESS_MESSAGE });
          if (fromCandidate) {
            void navigate({ to: '/admin/queue/$pieceId', params: { pieceId: fromCandidate } });
          } else {
            void navigate({ to: '/admin/queue' });
          }
        },
        onError: () => {
          emitToast({ message: SAVE_ERROR_MESSAGE });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="p-8 flex flex-col gap-5 max-w-[560px]">
      <FormInput
        label="Title"
        isRequired
        value={prettyName}
        onChange={(event) => setPrettyName(event.target.value)}
        error={prettyNameError}
      />
      <FormInput
        label="Canonical name"
        isRequired
        value={canonicalName}
        onChange={(event) => setCanonicalName(event.target.value)}
        error={canonicalNameError}
      />
      <FormInput
        label="Summary"
        value={summary}
        maxLength={SUMMARY_MAX_LENGTH}
        onChange={(event) => setSummary(event.target.value)}
      />

      <div className="flex flex-col gap-1.5">
        <Micro as="label" htmlFor={regionSelectId}>
          Region
        </Micro>
        <select
          id={regionSelectId}
          value={region}
          onChange={(event) => setRegion(event.target.value as PieceRegion | '')}
          className="h-11 px-3 bg-[var(--bg-surface)] border border-[var(--rule)] rounded-[var(--r-sm)] text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)]"
        >
          <option value={NO_REGION_VALUE}>— None —</option>
          {PIECE_REGIONS.map((value) => (
            <option key={value} value={value}>
              {REGION_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      <StringListEditor legend="Forms" addLabel="Add form" values={forms} onChange={setForms} />
      <StringListEditor
        legend="Also known as"
        addLabel="Add alternate name"
        values={aka}
        onChange={setAka}
      />

      <div className="flex items-center gap-4 pt-2">
        <button
          type="submit"
          disabled={updatePiece.isPending}
          className="inline-flex items-center h-11 px-5 rounded-[var(--r-sm)] bg-[var(--accent)] text-white text-[14px] font-semibold disabled:opacity-60"
        >
          {updatePiece.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {backLink(fromCandidate)}
      </div>
    </form>
  );
}

function AdminPieceEditScreen(): ReactElement {
  const { fromCandidate } = Route.useSearch();
  const candidate = useModerationCandidate(fromCandidate ?? '');
  const piece = candidate.data?.piece;

  return (
    <DAdminShell section="pieces" title="Edit piece">
      {candidate.isLoading || !piece ? (
        <div className="p-8 text-[13px] text-[var(--ink-3)]">Loading…</div>
      ) : (
        <PieceEditForm piece={piece} fromCandidate={fromCandidate} />
      )}
    </DAdminShell>
  );
}

export const Route = createFileRoute('/admin/pieces/edit')({
  component: AdminPieceEditScreen,
  validateSearch: (search: Record<string, unknown>): PieceEditSearch => ({
    fromCandidate: typeof search.fromCandidate === 'string' ? search.fromCandidate : undefined,
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});
